import { Component, EventEmitter, Inject, Input, OnInit, Output, signal } from '@angular/core'
import { HttpErrorResponse } from "@angular/common/http"
import { FormBuilder, FormGroup, Validators } from "@angular/forms"
import { EmailValidator } from "../validator/email-validator"
import { OrderService } from "../../services/order.service"
import { OrderPersistedInterface, RefundableOrderViewInterface, RefundSubmitItemInterface, } from "../../models/order"
import { RefundCartItem } from "../../models/cart";
import { PageService } from "@gea-waldviertler-website/shop-lib";
import { RefundReasonInterface } from "@gea-waldviertler-website/gea-models";

@Component({
  selector: 'gea-shop-refund-form',
  templateUrl: './refund.component.html',
  styleUrl: 'refund.component.scss',
  standalone: false
})
export class RefundFormComponent implements OnInit {

  @Output("submitEvent") submitEvent = new EventEmitter<undefined>()

  // optional prefill values, e.g. when opened from the customer's order list
  @Input() prefillOrderNumber?: string
  @Input() prefillEmail?: string

  form: FormGroup
  // first-/last name of the person submitting the refund (sent to the API)
  nameForm: FormGroup
  loading = signal<boolean>(false)
  successfullSubmitted: boolean = false
  errorMessage = signal<string | undefined>(undefined)
  successMessage = signal<string | undefined>(undefined)
  refundReasons = signal<RefundReasonInterface[]>([])

  // set after a matching order was found
  order = signal<OrderPersistedInterface | undefined>(undefined)
  orderUuid?: string
  items = signal<RefundCartItem[]>([])
  // selected amount per order item id
  selectedAmounts: { [id: number]: number } = {}
  // selected refund reason id per order item id (0 = not yet selected)
  selectedReasons: { [id: number]: number } = {}

  constructor(
    @Inject(OrderService) private orderService: OrderService,
    @Inject(PageService) private pageService: PageService,
    fb: FormBuilder
  ) {
    this.form = fb.group({
      orderNumberPart1: [ "", { validators: [ Validators.required, Validators.pattern(/^\d{4}$/) ] } ],
      orderNumberPart2: [ "", { validators: [ Validators.required, Validators.pattern(/^\d{4}$/) ] } ],
      orderNumberPart3: [ "", { validators: [ Validators.required, Validators.pattern(/^\d{6}$/) ] } ],
      email: [ "", { validators: [ Validators.required, EmailValidator.matchValidator ] } ],
    })

    this.nameForm = fb.group({
      firstname: [ "", { validators: [ Validators.required ] } ],
      lastname: [ "", { validators: [ Validators.required ] } ],
    })

    let bootstrapData = this.pageService.getBootstrapSignal()()
    if (bootstrapData?.config?.refundReasons) this.refundReasons.set(bootstrapData.config.refundReasons)
  }

  ngOnInit() {
    if (this.prefillOrderNumber && this.prefillEmail) {
      this.splitOrderNumber(this.prefillOrderNumber)
      this.form.patchValue({ email: this.prefillEmail })
      this.findOrder()
    }
  }

  // Splits a full order number (e.g. "1001-2026-000031") into the three parts.
  // Any non-digit separators are ignored, so pasting works regardless of format.
  private splitOrderNumber(value: string) {
    const match = (value || "").match(/(\d{4})\D*(\d{4})\D*(\d{6})/)
    if (! match) return
    this.form.patchValue({
      orderNumberPart1: match[1],
      orderNumberPart2: match[2],
      orderNumberPart3: match[3],
    })
  }

  // Handles paste of a full order number into any of the three fields.
  onOrderNumberPaste(event: ClipboardEvent) {
    const text = event.clipboardData?.getData("text") ?? ""
    if (/\d{4}\D*\d{4}\D*\d{6}/.test(text)) {
      event.preventDefault()
      this.splitOrderNumber(text)
    }
  }

  // True if any of the three order-number parts is invalid and was touched.
  orderNumberInvalid(): boolean {
    return [ "orderNumberPart1", "orderNumberPart2", "orderNumberPart3" ].some(name => {
      const control = this.form.controls[name]
      return control.invalid && control.touched
    })
  }

  private getOrderNumber(): string {
    const v = this.form.value
    return `${v.orderNumberPart1}-${v.orderNumberPart2}-${v.orderNumberPart3}`
  }

  findOrder() {
    if (! this.form.valid) {
      this.form.markAllAsTouched()
      return
    }

    this.loading.set(true)
    this.errorMessage.set(undefined)

    this.orderService.findRefundableOrder({
      orderNumber: this.getOrderNumber(),
      email: this.form.value.email
    }).subscribe(response => {
        this.loading.set(false)
        if (! response) {
          this.errorMessage.set('Es wurde keine passende Bestellung gefunden.')
          return
        }
        this.applyResponse(response)
      },
      (error: HttpErrorResponse) => {
        this.loading.set(false)
        this.errorMessage.set(this.getErrorMessage(error, 'Es wurde keine passende Bestellung gefunden.'))
      }
    )
  }

  private getErrorMessage(error: HttpErrorResponse, fallback: string): string {
    if (error?.status === 400 && error.error?.message) {
      return error.error.message
    }
    return fallback
  }

  private applyResponse(response: RefundableOrderViewInterface) {
    this.orderUuid = response.uuid
    this.order.set(response.order)
    this.items.set(response.order.cart.items)

    // prefill first-/last name from the customer record
    const customer = response.order.customer
    if (customer) {
      this.nameForm.patchValue({
        firstname: customer.firstname ?? "",
        lastname: customer.lastname ?? ""
      })
    }

    this.selectedAmounts = {}
    this.selectedReasons = {}
    for (const item of this.items()) {
      this.selectedAmounts[item.id] = 0
      this.selectedReasons[item.id] = 0
    }
  }

  isRefundable(item: RefundCartItem): boolean {
    return ! item.nonRefundableReason && item.amountRefundable > 0
  }

  getTitle(item: RefundCartItem): string {
    return item.distinctProduct?.product?.title ?? ''
  }

  getAmountOptions(item: RefundCartItem): number[] {
    const options: number[] = []
    for (let i = 0; i <= item.amountRefundable; i++) {
      options.push(i)
    }
    return options
  }

  setAmount(item: RefundCartItem, amount: number) {
    this.selectedAmounts[item.id] = amount
  }

  setReason(item: RefundCartItem, reason: number) {
    this.selectedReasons[item.id] = reason
  }

  hasSelection(): boolean {
    return this.getSelectedItems().length > 0
  }

  canSubmit(): boolean {
    return this.hasSelection() && this.nameForm.valid
  }

  private getSelectedItems(): RefundSubmitItemInterface[] {
    const items: RefundSubmitItemInterface[] = []
    for (const item of this.items()) {
      if (! this.isRefundable(item)) continue
      const amount = this.selectedAmounts[item.id]
      if (amount && amount > 0) {
        items.push({ id: item.id, amount: amount, reason: this.selectedReasons[item.id] })
      }
    }
    return items
  }

  submitRefund() {
    if (! this.orderUuid) return
    const items = this.getSelectedItems()
    if (! items.length) {
      this.errorMessage.set('Bitte wähle mindestens ein Produkt aus.')
      return
    }
    if (items.some(item => ! item.reason)) {
      this.errorMessage.set('Bitte wähle für jedes gewählte Produkt einen Grund aus.')
      return
    }

    if (! this.nameForm.valid) {
      this.nameForm.markAllAsTouched()
      this.errorMessage.set('Bitte gib deinen Vor- und Nachnamen an.')
      return
    }

    const order = this.order()
    if (! order || ! order.id) return

    this.loading.set(true)
    this.errorMessage.set(undefined)
    this.orderService.submitRefund({
      uuid: this.orderUuid,
      items: items,
      id: order.id,
      firstname: this.nameForm.value.firstname,
      lastname: this.nameForm.value.lastname
    }).subscribe(message => {
      this.loading.set(false)
      this.successfullSubmitted = true
      this.successMessage.set(message)
    },
    error => {
      this.loading.set(false)
      this.errorMessage.set(this.getErrorMessage(error, 'Beim Übermitteln des Widerrufs ist ein Fehler aufgetreten.'))
    })
  }
}
